import { useCallback, useMemo, useState } from 'react';

import { useLazyGetSubscriptionsForSaleQuery } from '@app/api/Subscriptions';
import type { SubscriptionSaleDraftItem } from '@app/components/RecordModal/components/RecordDetails/types';
import { FRONT_WARNING, WARNING } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { createId } from '@app/utils';

interface UseSubscriptionSaleItemsProps {
  items: SubscriptionSaleDraftItem[];
  onChange?(items: SubscriptionSaleDraftItem[]): void;
  activeBranchId: number | null;
}

// type, а не interface: опция Select должна иметь индексную сигнатуру
export type SubscriptionSaleOption = {
  value: number;
  label: string;
  price: number;
};

export interface SubscriptionSaleRow {
  item: SubscriptionSaleDraftItem;
  total: number;
  /** Абонемент уже продан клиенту: сам абонемент и продавец не меняются */
  isSold: boolean;
  /** Проданный абонемент оплачен (CanEditAndDelete = false) */
  isPaid: boolean;
  /** Скидку можно менять, строку — удалить: новая или неоплаченная */
  isEditable: boolean;
}

const roundMoney = (value: number) => Number(value.toFixed(2));

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const getSubscriptionSaleTotal = ({
  price,
  discountCurrency,
}: SubscriptionSaleDraftItem) => {
  const total = price - discountCurrency;

  return total > 0 ? roundMoney(total) : 0;
};

const createSubscriptionSaleDraftItem = (): SubscriptionSaleDraftItem => ({
  id: createId(),
  saleId: 0,
  price: 0,
  discountCurrency: 0,
  discountPercent: 0,
  sellerId: null,
  canEditAndDelete: true,
});

/**
 * Строки абонементов в окне записи. Список для продажи грузится по первому
 * «Добавить абонемент» (GetSubscriptionsForSale); количество всегда 1,
 * скидка — от 0 до цены, хранится в рублях и в процентах, как у товаров.
 */
export const useSubscriptionSaleItems = ({
  items,
  onChange,
  activeBranchId,
}: UseSubscriptionSaleItemsProps) => {
  const { showNotification } = useNotification();
  // Проданная строка удаляется только после окна подтверждения: после
  // сохранения записи бэкенд отменит продажу (SetRecordSubscriptions).
  const [itemIdToDelete, setItemIdToDelete] = useState<string | null>(null);
  // Название для текста окна запоминается при нажатии на корзину и не
  // сбрасывается при закрытии: текст не меняется, пока окно закрывается.
  const [nameToDelete, setNameToDelete] = useState<string | null>(null);
  const [
    getSubscriptionsForSale,
    { data: subscriptionsForSale, isFetching: isSubscriptionsLoading },
  ] = useLazyGetSubscriptionsForSaleQuery();

  const subscriptionOptions: SubscriptionSaleOption[] = useMemo(
    () =>
      (subscriptionsForSale ?? []).map(({ SubscriptionId, Name, Cost }) => ({
        value: SubscriptionId,
        label: Name ?? '',
        price: Cost,
      })),
    [subscriptionsForSale]
  );

  const rows: SubscriptionSaleRow[] = useMemo(
    () =>
      items.map((item) => {
        // Оплаченный абонемент бэкенд не даёт менять и удалять
        // (CanEditAndDelete, ТЗ us/11427).
        const isSold = item.saleId > 0;
        const isPaid = isSold && !item.canEditAndDelete;

        return {
          item,
          total: getSubscriptionSaleTotal(item),
          isSold,
          isPaid,
          isEditable: !isPaid,
        };
      }),
    [items]
  );

  const totalQuantity = useMemo(
    () => items.filter(({ subscriptionId }) => subscriptionId).length,
    [items]
  );

  const totalAmount = useMemo(
    () =>
      roundMoney(
        rows
          .filter(({ item }) => item.subscriptionId)
          .reduce((acc, { total }) => acc + total, 0)
      ),
    [rows]
  );

  const updateItem = useCallback(
    (
      itemId: string,
      handler: (item: SubscriptionSaleDraftItem) => SubscriptionSaleDraftItem
    ) => {
      onChange?.(
        items.map((item) => (item.id === itemId ? handler(item) : item))
      );
    },
    [items, onChange]
  );

  const addItemHandler = useCallback(async () => {
    onChange?.([...items, createSubscriptionSaleDraftItem()]);

    if (activeBranchId === null) {
      showNotification('Активный филиал не найден', FRONT_WARNING);
      return;
    }

    try {
      await getSubscriptionsForSale(
        { ActiveBranchId: activeBranchId },
        true
      ).unwrap();
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [
    items,
    onChange,
    activeBranchId,
    getSubscriptionsForSale,
    showNotification,
  ]);

  const removeItem = useCallback(
    (itemId: string) => {
      onChange?.(items.filter((item) => item.id !== itemId));
    },
    [items, onChange]
  );

  const deleteItemHandler = useCallback(
    (itemId: string) => {
      const item = items.find(({ id }) => id === itemId);

      if (item && item.saleId > 0) {
        setItemIdToDelete(itemId);
        setNameToDelete(item.name ?? null);
        return;
      }

      removeItem(itemId);
    },
    [items, removeItem]
  );

  const isDeleteConfirmOpen = useMemo(
    () => items.some(({ id }) => id === itemIdToDelete),
    [items, itemIdToDelete]
  );

  const deleteConfirmText = nameToDelete
    ? `Абонемент «${nameToDelete}» продан клиенту. После сохранения записи продажа отменится.`
    : 'Абонемент продан клиенту. После сохранения записи продажа отменится.';

  const confirmDeleteHandler = useCallback(() => {
    if (itemIdToDelete) removeItem(itemIdToDelete);

    setItemIdToDelete(null);
  }, [itemIdToDelete, removeItem]);

  const deleteModalOpenChangeHandler = useCallback((isOpen: boolean) => {
    if (!isOpen) setItemIdToDelete(null);
  }, []);

  const subscriptionChangeHandler = useCallback(
    (itemId: string, subscriptionId: number) => {
      const subscription = subscriptionOptions.find(
        ({ value }) => value === subscriptionId
      );

      if (!subscription) return;

      updateItem(itemId, (item) => ({
        ...item,
        subscriptionId,
        name: subscription.label,
        price: subscription.price,
        discountCurrency: 0,
        discountPercent: 0,
      }));
    },
    [subscriptionOptions, updateItem]
  );

  const sellerChangeHandler = useCallback(
    (itemId: string, sellerId?: number) => {
      updateItem(itemId, (item) => ({ ...item, sellerId: sellerId ?? null }));
    },
    [updateItem]
  );

  const discountCurrencyChangeHandler = useCallback(
    (itemId: string, value: number) => {
      updateItem(itemId, (item) => {
        const discountCurrency = clamp(value, 0, item.price);

        return {
          ...item,
          discountCurrency,
          discountPercent:
            item.price > 0 ? (discountCurrency / item.price) * 100 : 0,
        };
      });
    },
    [updateItem]
  );

  const discountPercentChangeHandler = useCallback(
    (itemId: string, value: number) => {
      updateItem(itemId, (item) => {
        const discountPercent = clamp(value, 0, 100);

        return {
          ...item,
          discountPercent,
          discountCurrency: roundMoney((item.price * discountPercent) / 100),
        };
      });
    },
    [updateItem]
  );

  return {
    rows,
    subscriptionOptions,
    isSubscriptionsLoading,
    totalQuantity,
    totalAmount,
    addItemHandler,
    deleteItemHandler,
    isDeleteConfirmOpen,
    deleteConfirmText,
    confirmDeleteHandler,
    deleteModalOpenChangeHandler,
    subscriptionChangeHandler,
    sellerChangeHandler,
    discountCurrencyChangeHandler,
    discountPercentChangeHandler,
  };
};
